/**
 * Feature Request Detail Route Definition
 * Route: /feature-requests/$featureRequestId
 */
import { Await, createFileRoute, useNavigate, useRouter } from '@tanstack/react-router';
import { v2 } from '@/lib/i18n/v2';
import { ErrorState } from '@/components/v2/error-state';
import {
  FeatureRequestDetailPageV2,
  FeatureRequestDetailSkeleton,
} from '@/components/v2/feature-requests/feature-request-detail-page';
import { featureRequestQuery } from '@/lib/queries/detail';
import { cacheFirst } from '@/lib/queries/cache-first';
import { PageHeader } from '@/components/v2/page-header';
import { BackButton } from '@/components/v2/page-header/page-header-buttons';
import type { ReactNode } from 'react';

type FeatureRequestDetailResult<T> = { ok: true; data: T } | { ok: false };
const toFeatureRequestDetailResult = async <T,>(
  promise: Promise<T>
): Promise<FeatureRequestDetailResult<T>> => {
  try {
    return { ok: true, data: await promise };
  } catch {
    return { ok: false };
  }
};

function FeatureRequestFrame({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  return (
    <div>
      <PageHeader
        leftAction={<BackButton onClick={() => navigate({ to: '/feature-requests' })} />}
      />
      {children}
    </div>
  );
}

function FeatureRequestPending() {
  return (
    <FeatureRequestFrame>
      <FeatureRequestDetailSkeleton />
    </FeatureRequestFrame>
  );
}

function FeatureRequestDetailPage() {
  const { featureRequestId } = Route.useParams();
  const { featureRequestDetailPromise } = Route.useLoaderData();
  const router = useRouter();
  return (
    <Await promise={featureRequestDetailPromise} fallback={<FeatureRequestPending />}>
      {(result) =>
        result.ok ? (
          <FeatureRequestDetailPageV2 featureRequestId={featureRequestId} />
        ) : (
          <FeatureRequestFrame>
            <div
              style={{
                minHeight: '70dvh',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <ErrorState
                severity="failure"
                title={v2.error.loadFailed('this feature request')}
                onRetry={() => void router.invalidate()}
                onHome={() => router.navigate({ to: '/' })}
              />
            </div>
          </FeatureRequestFrame>
        )
      }
    </Await>
  );
}

export const Route = createFileRoute('/_authenticated/feature-requests/$featureRequestId')({
  // Template-first pending while this route's component chunk downloads
  // (first visit): the destination's page shape instead of the full-page
  // RoutePending mascot. The in-component Await fallback takes over for data
  // streaming once the chunk lands. FeatureRequestPending (header + the
  // detail skeleton) IS the Await fallback rendered by this same def file, so
  // the chunk window and the data window hand off pixel-identically at zero
  // extra eager cost — the generic post-detail template read as the wrong
  // destination (title + vote rail + description, not a feed card).
  pendingComponent: FeatureRequestPending,
  loader: ({ context, params }) => {
    // Key + queryFn come from the shared factory (app/lib/queries/detail.ts) so
    // the loader-seeded entry, the detail page's useQuery and the data
    // prefetchers can never drift apart.
    const detailQuery = featureRequestQuery(params.featureRequestId);
    // Cache-first: a cached detail renders instantly and the plane heals
    // staleness in the background (single-flight-deduped with the detail page's
    // useQuery), so re-entry after the staleTime never re-suspends behind the
    // skeleton; a cold cache keeps the streamed loader fetch.
    const { cached, stream } = cacheFirst(context.queryClient, detailQuery.queryKey, () =>
      context.queryClient.ensureQueryData(detailQuery)
    );
    return {
      featureRequestDetailPromise: toFeatureRequestDetailResult(
        cached !== undefined ? Promise.resolve(cached) : stream()
      ),
    };
  },
  headers: () => ({ 'Cache-Control': 'private, no-store' }),
  component: FeatureRequestDetailPage,
});
